import { createCategorySchema, type CategoryDto } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormField } from '@/components/FormField';
import { ImageUpload } from '@/components/ImageUpload';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { catalog, useCatalogMutation } from './useCatalog';

type FormValues = z.input<typeof createCategorySchema>;

export function CategoryDialog({
  localId,
  open,
  onOpenChange,
  category,
}: {
  localId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Si viene, edita; si no, crea. */
  category: CategoryDto | null;
}) {
  const form = useForm<FormValues>({
    resolver: zodResolver(createCategorySchema),
    defaultValues: { name: '', description: '', imageUrl: '' },
  });
  const { errors } = form.formState;
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) form.reset({
        name: category?.name ?? '',
        description: category?.description ?? '',
        imageUrl: category?.imageUrl ?? '',
      });
  }, [open, category, form]);

  const save = useCatalogMutation(
    localId,
    (values: FormValues) => {
      const body = { ...values, imageUrl: values.imageUrl || null };
      return category ? catalog.updateCategory(localId, category.id, body) : catalog.createCategory(localId, body);
    },
    {
      success: category ? 'Categoría guardada' : 'Categoría creada',
      onSuccess: () => onOpenChange(false),
    },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
          <DialogDescription>Las categorías agrupan los productos en tu menú (p. ej. Helados, Postres, Cafetería).</DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-4" noValidate>
          <FormField label="Nombre" htmlFor="cat-name" error={errors.name?.message}>
            <Input id="cat-name" autoFocus maxLength={80} {...form.register('name')} />
          </FormField>
          <FormField label="Descripción (opcional)" htmlFor="cat-desc" error={errors.description?.message}>
            <Textarea id="cat-desc" rows={2} maxLength={300} {...form.register('description')} />
          </FormField>
          <FormField label="Imagen (opcional)" htmlFor="cat-img" error={errors.imageUrl?.message}>
            <Controller
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <ImageUpload
                  id="cat-img"
                  localId={localId}
                  value={field.value ?? ''}
                  onChange={(url) => field.onChange(url)}
                  error={errors.imageUrl?.message}
                  onBusyChange={setUploading}
                />
              )}
            />
          </FormField>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={save.isPending || uploading}>
              {save.isPending && <Spinner />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
